/* ───────────────────────────────────────────────────────────────────────────
   O16 bundle ladder — make the chosen row the thing that gets bought
   ─────────────────────────────────────────────────────────────────────────────
   snippets/o16-bundle-ladder.liquid renders a column of radios. Row one is
   this product; every row after it is a different product entirely, chosen by
   the owner in the product's own metafield. This file is what turns that
   choice into a purchase.

   ONE INPUT, EVERY PURCHASE PATH
   The theme has three ways to buy from a product page — add to cart
   (assets/product-form.js), the express wallets, and the mobile sticky bar,
   which is not a copy but the REAL buy row pinned in place
   (assets/o16-sticky-buybar.js). All three submit the same <product-form>,
   and all three read the same `[name=id]` input inside it. So the ladder does
   not implement adding to cart at all: it writes the chosen variant id into
   that one input and lets the theme do what it already does. No second cart
   call to keep in step, no path that can quietly miss the choice.

   HANDING CONTROL BACK
   Row one carries no fixed id. This product has a Language picker, and the
   variant it resolves to changes while the shopper uses it, so pinning the
   id that was current at page load would sell the wrong language to anyone
   who switched after choosing "just this one". Instead the self row restores
   whatever the theme's own variant picker currently says, and keeps doing so
   on every `variant:change` while it stays selected.

   THE VARIANT PICKER STAYS LIVE (owner 2026-09-09)
   It was briefly dimmed while a bundle was chosen, on the reasoning that a
   bundle is one fixed variant of another product and the picker cannot reach
   inside it. The owner overruled that, and rightly: a control that has gone
   grey for a reason the shopper cannot see is worse than one that does
   something sensible. Choosing a size or a language now does what it always
   does — moves this product to that variant — and the ladder simply returns to
   "just this one" on the newly chosen variant, with that variant's own sets
   below it.

   THE EXTENDED-DELIVERY GATE MOVES WITH THE CHOICE
   A bundle can itself be a pre-order or back-order. The consent gate and the
   record it writes onto the line item key off data attributes on
   <product-form>, refreshed per variant by
   assets/o16-extended-delivery-consent.js. We call that same function rather
   than reproducing any of its rules, and the bundle products' variants are
   already in the map it reads (snippets/main-product-section.liquid appends
   them). Without this a back-order bundle would sell as an ordinary in-stock
   item with no consent and no record — the #7459 hole, reopened.

   THE PAGE RENDERS TWICE
   The product section exists twice, a desktop wrapper and a mobile one, with
   CSS hiding whichever does not apply. Every query here is a querySelectorAll
   over the whole document for exactly that reason: a choice made in the copy
   the shopper can see has to land in both forms, because either one may be
   the one that submits.
─────────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  var LADDER = '[data-o16-bundle-ladder]';

  // The variant the theme's own picker currently points at. Read once from
  // the self row's server-rendered value, then kept current by the same
  // `variant:change` event every other O16 PDP file listens to
  // (assets/global.js, VariantSelects.onVariantChange — a plain DOM
  // CustomEvent on document, NOT Dawn's PUB_SUB_EVENTS).
  var themeVariant = null;

  // What the mobile sticky bar shows for THIS product, so picking a bundle and
  // going back to row one is reversible. Seeded from the server-rendered bar
  // at init, then kept current on every `variant:change`.
  var themeBar = { title: null, priceHTML: null };

  // The add button as the THEME last left it for this product's own variant —
  // its label and whether it was disabled. Restored verbatim when the shopper
  // goes back to row one, so a sold-out product does not come back sellable
  // just because a bundle was looked at. Re-captured after every
  // `variant:change` (see the listener), because that is the moment the theme
  // sets it correctly for the newly chosen variant.
  var themeButton = { label: null, disabled: null };

  // Whether row one was the selected row before the change now being handled.
  // The page always loads on row one. Needed because by the time a `change`
  // event fires the radio group has already moved, so the DOM can no longer
  // say where the shopper came FROM — and capturing on every bundle click
  // would capture this file's own label the second time round.
  var wasOnSelf = true;

  /* Only the ladder belonging to the variant on screen. A product whose
     variants offer different sets renders one ladder per variant, all but the
     chosen one hidden (see the snippet), so every query here has to ignore the
     hidden ones — otherwise a hidden ladder's checked row would answer for the
     visible one and the shopper would buy something they cannot see. */
  function ladders() {
    return Array.prototype.filter.call(
      document.querySelectorAll(LADDER),
      function (el) {
        var scope = el.closest('[data-o16-bundle-scope]');
        return !scope || !scope.hasAttribute('hidden');
      }
    );
  }

  /* Show the chosen variant's ladder and hide the rest. A variant the owner
     left empty has no ladder at all, so nothing becomes visible — which is the
     rule: no sets on this variant means the feature is not there for it. */
  function showScopeForVariant(variantId) {
    var scopes = document.querySelectorAll('[data-o16-bundle-scope]');
    if (!scopes.length) return;
    scopes.forEach(function (scope) {
      var mine = String(scope.getAttribute('data-o16-bundle-scope')) === String(variantId);
      if (mine) scope.removeAttribute('hidden');
      else scope.setAttribute('hidden', 'hidden');
      // A hidden ladder must not be reachable by keyboard either. A row the
      // server disabled because its set is sold out stays disabled when the
      // ladder comes back — markDisabledRows() records that once, so showing a
      // scope can never make an unbuyable set buyable.
      scope.querySelectorAll('input[type="radio"]').forEach(function (i) {
        i.disabled = mine ? i.dataset.o16BundleSoldOut === 'true' : true;
      });
    });
  }

  function forms() {
    return document.querySelectorAll('product-form');
  }

  function checkedInput() {
    // First checked row of the VISIBLE ladder wins. The two copies of the
    // section are kept in step by mirror() below, so "first" and "the one the
    // shopper clicked" are the same row by the time anything reads this.
    var found = null;
    ladders().forEach(function (l) {
      if (found) return;
      found = l.querySelector('input:checked');
    });
    return found;
  }

  function isSelf(input) {
    return !input || input.dataset.o16BundleSelf === 'true';
  }

  /* ── THE SET FOLLOWS THE SIZE ──────────────────────────────────────────
     A set that comes in sizes has to be added in the size the shopper is
     looking at. The row carries its whole table (see the snippet); this picks
     the entry whose option value matches one of the current variant's, and
     moves both the row's target id and the price it shows.

     Matching on VALUE, not on option name: a product may call it "Size" and a
     set may call it "Diameter", but both say "Ø28", and that is the only thing
     that has to agree. A row whose set has one variant carries no table and is
     left exactly as the server rendered it. */
  function parseRowVariants(attr) {
    var map = {};
    if (!attr) return map;
    attr.split(';').forEach(function (entry) {
      if (!entry) return;
      var parts = entry.split(':');
      if (parts.length < 3) return;
      var id = parts[parts.length - 2];
      var price = parts[parts.length - 1];
      var value = parts.slice(0, parts.length - 2).join(':');
      if (value && id) map[value] = { id: id, price: parseInt(price, 10) };
    });
    return map;
  }

  function currentOptionValues() {
    if (themeVariant && themeVariant.options && themeVariant.options.length) {
      return themeVariant.options.slice();
    }
    if (themeVariant && themeVariant.title) {
      return String(themeVariant.title).split(' / ');
    }
    // Page load: the theme has not announced anything yet, so read the picker.
    var values = [];
    document.querySelectorAll('variant-radios input:checked, variant-selects select').forEach(function (el) {
      if (el.value) values.push(el.value);
    });
    return values;
  }

  function retargetRowsToCurrentSize() {
    var values = currentOptionValues();
    if (!values.length) return;

    var sized = [];
    ladders().forEach(function (l) {
      sized = sized.concat(Array.prototype.slice.call(l.querySelectorAll('input[data-o16-bundle-variants]')));
    });
    sized.forEach(function (input) {
      var map = parseRowVariants(input.getAttribute('data-o16-bundle-variants'));
      var hit = null;
      for (var i = 0; i < values.length && !hit; i++) {
        if (map[values[i]]) hit = map[values[i]];
      }
      if (!hit) return;

      input.dataset.o16BundleVariant = hit.id;
      input.value = hit.id;
      if (isFinite(hit.price)) input.dataset.o16BundlePrice = hit.price;

      var priceEl = input.parentNode.querySelector('.o16-bundle-ladder__price');
      var priceHTML = money(hit.price);
      if (priceEl && priceHTML != null) priceEl.innerHTML = priceHTML;
    });
  }

  /* Put the id into every form's `[name=id]`. That input is what
     assets/product-form.js puts in its FormData and what the express wallets
     submit, so this single write covers both. */
  function applyVariantToForms(variantId) {
    if (!variantId) return;
    forms().forEach(function (form) {
      var input = form.querySelector('[name="id"]');
      if (input && input.value !== String(variantId)) input.value = String(variantId);
    });
  }

  /* Keep the two copies of the section showing the same choice. Without this
     a shopper who resizes across the breakpoint would find the other copy
     still on row one while the form carries a bundle. */
  function mirror(index) {
    ladders().forEach(function (ladder) {
      var inputs = ladder.querySelectorAll('input[type="radio"]');
      var target = inputs[index];
      if (target && !target.checked && !target.disabled) target.checked = true;
    });
  }

  /* The mobile sticky bar shows the variant name and price of what is about
     to be bought. It fills those from `variant:change`, which never fires for
     a bundle (a bundle is not a variant of this product), so it would sit
     there saying "ENGLISH · 329,00 kr" over a button that buys a 899,00 kr
     set. Same two elements, same formatMoney the bar itself uses. */
  function money(cents) {
    if (typeof formatMoney !== 'function' || !isFinite(cents)) return null;
    return formatMoney(cents, (window.theme && window.theme.moneyFormat) || '');
  }

  function syncStickyBar(input) {
    var nameEls = document.querySelectorAll('.js-o16-buybar-variant');
    var priceEls = document.querySelectorAll('.js-o16-buybar-price');
    if (!nameEls.length && !priceEls.length) return;

    var title;
    var priceHTML;
    if (isSelf(input)) {
      // What the bar said for this product before a bundle was ever picked,
      // kept current by `variant:change`. Falling back to the seed read off
      // the server-rendered bar means going back to row one restores exactly
      // what was there, even on a page where the picker was never touched.
      title = themeBar.title;
      priceHTML = themeBar.priceHTML;
    } else {
      title = input.dataset.o16BundleTitle || '';
      priceHTML = money(parseInt(input.dataset.o16BundlePrice, 10));
    }

    if (title != null) nameEls.forEach(function (el) { el.textContent = title; });
    if (priceHTML != null) priceEls.forEach(function (el) { el.innerHTML = priceHTML; });
  }

  /* The theme's own contract for this button (assets/global.js,
     toggleAddButton): the button is [name="add"] and its label is that
     button's own > span. Mirrored exactly rather than reinvented, so the two
     can never disagree about which element carries the label. */
  function addButtons() {
    return document.querySelectorAll('.product-form__submit[name="add"], product-form [name="add"]');
  }

  /* Read the button as it stands RIGHT NOW. Only ever called while row one is
     the selected row, which is precisely when the theme owns the button and
     what it says is the truth for this product's current variant.

     Deliberately not called from the `variant:change` listener: this theme
     re-renders the section over a fetch on a variant switch, so anything read
     in that same tick is the PREVIOUS variant's button. Reading at the moment
     the shopper leaves row one instead needs no guess about when that fetch
     lands — by then the markup has long settled. Proven by the DANISH case:
     the timed version restored "Add to cart" over a sold-out variant. */
  function captureThemeButton() {
    var btn = addButtons()[0];
    if (!btn) return;
    var span = btn.querySelector(':scope > span');
    themeButton.label = span ? span.innerHTML : null;
    themeButton.disabled = btn.hasAttribute('disabled');
  }

  /* Row one keeps whatever the theme says — its price is already the big one
     under the title, so there is nothing to reconcile. A bundle row is the
     only case where the button would otherwise promise this product's price
     over a different product's basket, so that is the only case that gets the
     price spelled out. Slash-separated, the same pattern the launch page and
     the rest of this page use.

     Availability comes with it: a bundle row that cannot be bought is already
     a disabled input and cannot be reached, so any bundle the shopper CAN
     select is one the button must be open for — even on a product whose own
     variant is sold out, which is exactly when a bundle is worth the most. */
  function syncAddButton(input) {
    var btns = addButtons();
    if (!btns.length) return;

    if (isSelf(input)) {
      if (themeButton.label == null) return;
      btns.forEach(function (btn) {
        var span = btn.querySelector(':scope > span');
        if (span) span.innerHTML = themeButton.label;
        if (themeButton.disabled) btn.setAttribute('disabled', 'disabled');
        else btn.removeAttribute('disabled');
      });
      return;
    }

    var priceHTML = money(parseInt(input.dataset.o16BundlePrice, 10));
    var base = (window.variantStrings && window.variantStrings.addToCart) || 'Add to cart';
    btns.forEach(function (btn) {
      var span = btn.querySelector(':scope > span');
      if (span) {
        span.textContent = base;
        if (priceHTML != null) {
          span.innerHTML = span.innerHTML + ' <span class="o16-bundle-ladder__cta-sep">/</span> ' + priceHTML;
        }
      }
      btn.removeAttribute('disabled');
    });
  }

  function apply(input) {
    if (!input) return;

    var variantId = isSelf(input)
      ? (themeVariant ? themeVariant.id : input.dataset.o16BundleVariant)
      : input.dataset.o16BundleVariant;

    applyVariantToForms(variantId);
    syncStickyBar(input);
    syncAddButton(input);

    // Order matters: the flag has to be right for the variant now in the form
    // BEFORE the record that reads it is rewritten. Both are the consent
    // file's own functions; nothing about the gate is decided here.
    var ext = window.O16ExtendedDelivery;
    if (ext) {
      if (typeof ext.syncExtendedFlagForVariant === 'function') {
        ext.syncExtendedFlagForVariant(variantId);
      }
      if (typeof ext.syncConsentProperty === 'function') {
        ext.syncConsentProperty();
      }
    }
  }

  /* ── PAGE TAKEOVER (off unless the block setting says otherwise) ────────
     Choosing a set replaces this section with that product's OWN section,
     fetched from Shopify already rendered (`?section_id=`), and pushes its
     URL. Not a patchwork of swapped fields: the markup that lands is the
     same markup that product's page would have served, so its pictures,
     price, text and pre/back-order data are right by construction rather
     than by us copying them across correctly.

     Measured on DEV: ~650 ms to fetch, ~60 ms to insert. Everything built as
     a custom element (product-form above all) re-upgrades itself; the gallery
     keeps working; only the mobile sticky bar needs waking, which is why
     o16-sticky-buybar.js now exposes an init.

     pushState rather than replaceState, so Back returns to the product the
     shopper actually arrived on instead of skipping past it. */
  function takeoverEnabled() {
    var on = false;
    ladders().forEach(function (l) { if (l.hasAttribute('data-o16-bundle-takeover')) on = true; });
    return on;
  }

  var takeoverBusy = false;

  /* THE WAY BACK — an explicit link, not a row (owner 2026-09-09, second round).

     The first version pinned the row the shopper came from to the top of the
     ladder. It worked, and it was invisible: the ladder is a CHOICE control,
     every row in it means "this is what the button adds", and a row that
     secretly also means "go back" asks one element to say two things. On a
     phone an unselected row at the top reads as an option you did not pick,
     never as a way home. The owner had seen the same hole on other shops:
     you follow a set, and there is no way back to the thing you were looking
     at.

     So the way back is now a line that says where it goes — "Back to
     TEGLIA 1 Ø22" — and the ladder is left alone: the rows stay in the order
     the owner curated, the row the shopper came from stays wherever it was
     put, and the checked row is always what is on screen.

     IT IS THE PHONE'S OWN BACK BUTTON. Clicking it calls history.back(), so
     our link and the browser's arrow are the same action rather than two
     back buttons that disagree — and because the theme keeps `?variant=` in
     the address (global.js updateURL, replaceState), going back lands on the
     exact SIZE the shopper left, not the product's first one.

     The trail is held in memory on purpose: it belongs to this browsing move,
     not to the tab. A real page load is a new starting point and shows no
     link, which is right — there is nothing behind it. And if we ever cannot
     tell where we are in the journey (a forward press, a jump), the trail is
     dropped and the link disappears rather than naming the wrong product. */
  var trail = [];

  /* Whether this file has ever pushed a history entry. Without it a popstate
     raised by something else on the page would send us swapping sections on a
     product that never had a ladder. */
  var tookOver = false;

  function here() {
    return window.location.pathname + window.location.search;
  }

  /* What this page should be CALLED in a back link: the product's own name,
     plus the variant when it has one worth saying. The name is read from the
     ladder's own self row rather than the page title, because that is the
     wording the owner already chose for this product. */
  function pageLabel() {
    var src = null;
    ladders().forEach(function (l) {
      if (!src) src = l.querySelector('input[data-o16-bundle-self]');
    });
    if (!src) {
      ladders().forEach(function (l) {
        if (!src) src = l.querySelector('input[type="radio"]:checked');
      });
    }
    var name = '';
    if (src && src.nextElementSibling) {
      var n = src.nextElementSibling.querySelector('.o16-bundle-ladder__name');
      name = n ? n.textContent.trim() : '';
    }
    if (!name) {
      // A page whose product has no ladder still has to be nameable — it is
      // exactly the page a shopper gets stuck on.
      var h1 = document.querySelector('h1');
      name = h1 ? h1.textContent.replace(/\s+/g, ' ').trim() : '';
    }
    if (!name) return '';
    var vt = (themeVariant && themeVariant.title) || themeBar.title || '';
    vt = String(vt).trim();
    if (vt && vt.toLowerCase() !== 'default title') name += ' ' + vt;
    return name;
  }

  /* WHERE THE LINK LIVES — under the buy buttons, where the ladder is, and NOT
     inside the ladder.

     Proved on DEV: the set a shopper follows very often has no ladder of its
     own (the owner curates which products get one), and a way back that only
     exists inside a ladder is missing on exactly the page where being stuck is
     the whole problem. So the link is hung off the buy-buttons block instead,
     which every product page has — the same spot on the page whether a ladder
     follows it or not.

     One per rendered copy of the section: the PDP renders twice, a desktop
     wrapper and a mobile one, and the shopper may be looking at either. */
  function backHosts() {
    var section = document.querySelector('[id^="shopify-section-template--"][id$="__main"]');
    var scope = section || document;
    return Array.prototype.map.call(scope.querySelectorAll('product-form'), function (pf) {
      return pf.closest('[data-shopify-editor-block]') || pf.parentElement;
    }).filter(function (el, i, arr) { return el && arr.indexOf(el) === i; });
  }

  function renderBack() {
    var top = trail.length ? trail[trail.length - 1] : null;
    var wanted = top && top.label;

    document.querySelectorAll('[data-o16-bundle-back]').forEach(function (link) {
      if (!wanted && link.parentNode) link.parentNode.removeChild(link);
    });
    if (!wanted) return;

    backHosts().forEach(function (host) {
      // The link this host already owns is the one right after it — never a
      // sibling belonging to the other copy of the section.
      var link = host.nextElementSibling;
      if (!link || !link.hasAttribute('data-o16-bundle-back')) {
        link = document.createElement('a');
        link.className = 'o16-bundle-ladder__back';
        link.setAttribute('data-o16-bundle-back', '');
        host.parentNode.insertBefore(link, host.nextSibling);
      }
      // A real href, so the link survives a shopper who opens it in a new tab
      // and so it still goes somewhere if the history move is refused.
      link.href = top.url;
      link.textContent = 'Back to ' + top.label;
    });
  }

  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('[data-o16-bundle-back]');
    if (!link) return;
    if (!trail.length) return; // let the href do its ordinary work
    event.preventDefault();
    window.history.back();
  });

  function swapTo(url, push) {
    var host = document.querySelector('[id^="shopify-section-template--"][id$="__main"]');
    if (!url || !host || takeoverBusy) return false;
    takeoverBusy = true;

    /* A chosen set no longer arrives with a jump (owner 2026-09-09). The section
       dims the instant the row is touched — the same moment a link would have
       begun a navigation — and comes back at full strength once the new product
       is in place and standing still. The ~650 ms fetch used to pass in complete
       silence, which read as a page that had stopped working rather than one
       that was doing as it was told. Suppressed under prefers-reduced-motion. */
    host.classList.add('o16-pdp-swap');
    void host.offsetHeight; // let the starting opacity paint before it changes
    host.classList.add('o16-pdp-swap--out');

    /* THE PAGE GOES TO THE TOP (owner 2026-09-13, reversing his own call from
       the morning).

       We first kept the shopper's scroll position and put the ladder back under
       their finger, on the reasoning that they had changed product, not page.
       On a phone it read wrong: the pictures, the title and the price all
       change while the eye is parked halfway down, and the new product's own
       page never introduces itself. A takeover IS a page change to the person
       looking at it, so it behaves like one. */

    // Remembered BEFORE the page changes under us: this is the last moment
    // the DOM can still say what the shopper is looking at.
    if (push) {
      trail.push({ url: here(), label: pageLabel() });
      tookOver = true;
    }

    var sectionId = host.id.replace('shopify-section-', '');
    // The address may already carry `?variant=` — the way back keeps the size
    // the shopper chose, so the section request has to be joined, not appended.
    var sep = url.indexOf('?') > -1 ? '&' : '?';
    fetch(url + sep + 'section_id=' + encodeURIComponent(sectionId))
      .then(function (res) {
        if (!res.ok) throw new Error('section ' + res.status);
        return res.text();
      })
      .then(function (html) {
        /* `?section_id=` answers with the section's OWN wrapper element
           (<section id="shopify-section-…" class="shopify-section">), while the
           page already has one (<div id="shopify-section-…" class="shopify-section
           product-section">). Dropping the response in whole nested a section
           inside a section and left the inner copy without the `product-section`
           class this theme scopes so much of its CSS to. It looked right and
           measured wrong: 375px clean before a takeover, 383px after — eight
           pixels of horizontal overflow that a normal load of the very same page
           does not have.

           Taking the wrapper's CONTENT gives the host exactly the children a real
           page load would give it, so there is nothing left to differ. */
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var wrapper = parsed.body.firstElementChild;
        host.innerHTML =
          wrapper && /^shopify-section-/.test(wrapper.id || '')
            ? wrapper.innerHTML
            : html;
        if (push) window.history.pushState({ o16BundleLadder: true }, '', url);

        // Wake what cannot wake itself. Everything else on this page is either
        // a custom element or reads the DOM on demand.
        if (window.O16StickyBuybar && typeof window.O16StickyBuybar.init === 'function') {
          window.O16StickyBuybar.init();
        }
        if (window.O16ExtendedDelivery && typeof window.O16ExtendedDelivery.syncConsentProperty === 'function') {
          window.O16ExtendedDelivery.syncConsentProperty();
        }
        // The new title has never been measured. Left unfitted, a long one
        // ("THE COLLECTION") ran 8px past the right edge at 375px.
        if (window.O16PdpTitleFit && typeof window.O16PdpTitleFit.refit === 'function') {
          window.O16PdpTitleFit.refit();
        }
        /* The price/inventory/SKU mirror watches NODES, and the swap above
           just replaced them. Without this the mobile copy — the one most
           shoppers are looking at — keeps showing the size's price from
           before the takeover while the desktop copy updates correctly. */
        if (window.O16MobilePriceSync && typeof window.O16MobilePriceSync.init === 'function') {
          window.O16MobilePriceSync.init();
        }
        /* The gallery that decides which videos may load is bound to the old
           section's elements; the swap replaced them. Without this the new
           product's videos are ungated — the very thing the gate exists to
           stop (56 MB of autoplay video on a page nobody asked it of). */
        if (window.O16PdpVideoGate && typeof window.O16PdpVideoGate.init === 'function') {
          window.O16PdpVideoGate.init();
        }

        trackViewedProduct();

        // The new section brought its own ladder, its own row one and its own
        // theme state. Nothing carries over from the page we just left.
        themeVariant = null;
        themeBar = { title: null, priceHTML: null };
        themeButton = { label: null, disabled: null };
        wasOnSelf = true;

        markDisabledRows();
        retargetRowsToCurrentSize();
        renderBack();

        // The new product starts at its own beginning, exactly as it would
        // after a real page load.
        window.scrollTo({ top: 0, behavior: 'auto' });

        // In place and standing still: let it come up.
        host.classList.remove('o16-pdp-swap--out');
      })
      .catch(function () {
        // A takeover that cannot render is not worth a broken page: fall back
        // to the plain navigation the shopper was asking for anyway.
        window.location.href = url;
      })
      .then(function () {
        takeoverBusy = false;
      });

    return true;
  }

  /* A takeover is a product view that no page load announces, so nothing that
     counts views on load counts it. Klaviyo's own onsite API is the one that
     matters here: its browse-abandonment and back-in-stock flows are driven by
     these two events, and without them a shopper could study a set for minutes
     and Klaviyo would believe they never saw it.

     The payload is read from the section's own server-rendered JSON (see
     snippets/main-product-section.liquid) rather than scraped from the DOM —
     a formatted price is not a number, and guessing it back is how tracking
     data quietly goes wrong.

     Shopify's OWN analytics deliberately gets nothing: standard events like
     product_viewed are emitted by Shopify itself on page load and cannot be
     raised from theme code, so its counts for the sets stay low. That is a
     platform limit, not something to fake — a fabricated event would be worse
     than a missing one. */
  function trackViewedProduct() {
    var el = document.querySelector('script[data-o16-viewed-product]');
    if (!el || typeof window._learnq === 'undefined') return;

    var item;
    try {
      item = JSON.parse(el.textContent);
    } catch (err) {
      return; // never let tracking break the page
    }
    if (!item || !item.ProductID) return;

    try {
      window._learnq.push(['track', 'Viewed Product', item]);
      window._learnq.push([
        'trackViewedItem',
        {
          Title: item.Name,
          ItemId: item.ProductID,
          Categories: item.Categories,
          ImageUrl: item.ImageURL,
          Url: item.URL,
          Metadata: {
            Brand: item.Brand,
            Price: item.Price,
            CompareAtPrice: item.CompareAtPrice
          }
        }
      ]);
    } catch (err) {
      // A tracking call must never break a product page.
    }
  }

  function takeover(input) {
    return swapTo(input.dataset.o16BundleUrl, true);
  }

  /* Back and forward move between URLs this file pushed, and pushState alone
     changes the address without touching the page — so without this the
     shopper would land on /the-full-collection still looking at THE ORIGINALS.
     Proven on DEV before it existed. Swapping rather than reloading keeps the
     way back as quick as the way forward. */
  window.addEventListener('popstate', function () {
    if (!tookOver) return;
    var herePath = window.location.pathname;

    /* Keep the trail honest. Landing on the product the trail's top names is a
       step back, so that step is spent. Landing anywhere else means we can no
       longer say where the shopper is in the journey — a forward press, a jump
       — and then the link goes away rather than naming the wrong product. */
    if (trail.length && trail[trail.length - 1].url.split('?')[0] === herePath) trail.pop();
    else trail.length = 0;

    var rendered = null;
    ladders().forEach(function (l) { if (!rendered) rendered = l.querySelector('input[data-o16-bundle-self]'); });
    var renderedUrl = rendered && rendered.dataset.o16BundleUrl;
    // The rendered row one knows which product is actually on screen; only its
    // absence or a mismatch means the address has moved out from under it.
    if (renderedUrl && renderedUrl.split('?')[0] === herePath) { renderBack(); return; }
    swapTo(here(), false);
  });

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input || input.type !== 'radio') return;
    if (!input.closest || !input.closest(LADDER)) return;

    if (!isSelf(input) && takeoverEnabled()) {
      if (takeover(input)) return;
    }

    // Leaving row one is the last moment the button still belongs to the
    // theme, so it is the right moment to remember what it said — and the
    // ONLY moment, because from a bundle row onwards the label is this file's
    // own and capturing it again would make row one inherit a bundle's price.
    if (wasOnSelf && !isSelf(input)) captureThemeButton();
    wasOnSelf = isSelf(input);

    var siblings = input.closest(LADDER).querySelectorAll('input[type="radio"]');
    mirror(Array.prototype.indexOf.call(siblings, input));
    apply(input);
  });

  // The theme's picker moved. Remember where it is now, and if the shopper is
  // on "just this one" let that move straight through to the form — the theme
  // has already written its own id there, so this only has to keep the sticky
  // bar and the gate honest.
  document.addEventListener('variant:change', function (event) {
    var variant = event && event.detail && event.detail.variant;
    if (!variant) return;
    themeVariant = variant;
    // Scope first: the ladder that applies may be a different one entirely, and
    // everything below reads "the visible ladder".
    showScopeForVariant(variant.id);
    // Then the sets that come in sizes, which now point somewhere else.
    retargetRowsToCurrentSize();
    themeBar.title = variant.title || '';
    var vPrice = money(variant.price);
    if (vPrice != null) themeBar.priceHTML = vPrice;

    // Either way the form needs rewriting: on row one because the theme's new
    // variant is the thing being bought, and on a bundle row because the theme
    // has just overwritten the form's id with a variant of THIS product and
    // the bundle has to go back in.
    // On row one the theme owns the button and we leave it entirely alone —
    // apply() restores nothing there until something has been captured, which
    // only happens when the shopper actually leaves row one.
    var current = checkedInput();
    if (!current) return;
    // Picking a size or a language means the shopper wants THIS product in that
    // variant. Holding on to a chosen set would fight that: the picker would
    // appear to do nothing, which is the very trap dimming it was meant to
    // avoid. So the ladder returns to row one, on the variant just chosen.
    if (!isSelf(current)) {
      var selfInput = null;
      ladders().forEach(function (l) { if (!selfInput) selfInput = l.querySelector('input[data-o16-bundle-self]'); });
      if (selfInput) {
        var group = selfInput.closest(LADDER).querySelectorAll('input[type="radio"]');
        selfInput.checked = true;
        mirror(Array.prototype.indexOf.call(group, selfInput));
        current = selfInput;
      }
    }
    wasOnSelf = isSelf(current);
    apply(current);
  });

  /* Remember which rows the server rendered as unbuyable, before any scope
     toggling can blur the two kinds of "disabled" together. */
  function markDisabledRows() {
    document.querySelectorAll(LADDER + ' input[type="radio"]').forEach(function (i) {
      if (i.dataset.o16BundleSoldOut === undefined) {
        i.dataset.o16BundleSoldOut = i.disabled ? 'true' : 'false';
      }
    });
  }

  function init() {
    if (!document.querySelector(LADDER)) return;
    markDisabledRows();

    // Seed from what the server rendered, so the first `variant:change` is not
    // a prerequisite for row one to know its own id.
    var self = null;
    ladders().forEach(function (l) { if (!self) self = l.querySelector('input[data-o16-bundle-self]'); });
    if (self) themeVariant = { id: self.dataset.o16BundleVariant };

    var barName = document.querySelector('.js-o16-buybar-variant');
    var barPrice = document.querySelector('.js-o16-buybar-price');
    if (barName) themeBar.title = barName.textContent;
    if (barPrice) themeBar.priceHTML = barPrice.innerHTML;

    retargetRowsToCurrentSize();
    renderBack();

    var current = checkedInput();
    // Only act if the page did NOT load on row one — a fresh load on "just
    // this one" must leave the form exactly as the theme rendered it.
    if (current && !isSelf(current)) apply(current);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
